'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { Clock, RefreshCw, LogOut } from 'lucide-react';

const WARN_SECONDS = 5 * 60;

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function SessionGuard() {
  const { user, sessionExp, refreshSession, signOut } = useAuth();
  const router = useRouter();
  const [remaining, setRemaining] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const computeRemaining = useCallback(() => {
    if (!sessionExp) return null;
    return Math.max(0, sessionExp - Math.floor(Date.now() / 1000));
  }, [sessionExp]);

  useEffect(() => {
    if (!user || !sessionExp) {
      setRemaining(null);
      setShowWarning(false);
      return;
    }

    const tick = () => {
      const secs = computeRemaining();
      setRemaining(secs);
      if (secs === null) return;
      if (secs <= 0) {
        setShowWarning(false);
        router.replace('/session-expired');
      } else if (secs <= WARN_SECONDS) {
        setShowWarning(true);
      }
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, sessionExp, computeRemaining, router]);

  const handleContinue = async () => {
    setRefreshing(true);
    try {
      await refreshSession();
      setShowWarning(false);
    } finally {
      setRefreshing(false);
    }
  };

  if (!showWarning || remaining === null) return null;

  const urgent = remaining <= 60;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className={`h-1 ${urgent ? 'bg-red-500' : 'bg-amber-400'} transition-colors duration-500`} />

        <div className="px-6 py-7 flex flex-col items-center text-center gap-5">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-xl ${urgent ? 'bg-red-500/20 border border-red-500/40' : 'bg-amber-400/20 border border-amber-400/40'}`}>
            <Clock size={32} className={urgent ? 'text-red-400' : 'text-amber-400'} strokeWidth={1.5} />
          </div>

          <div>
            <h2 className="text-lg font-bold text-white">Session Expiring Soon</h2>
            <p className="text-sm text-slate-400 mt-1">Your session will expire in</p>
            <p className={`text-4xl font-mono font-bold mt-2 tabular-nums ${urgent ? 'text-red-400' : 'text-amber-400'}`}>
              {formatTime(remaining)}
            </p>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            Click &ldquo;Continue Session&rdquo; to stay logged in, or your work will be interrupted when the timer reaches zero.
          </p>

          <div className="flex flex-col w-full gap-2">
            <button
              onClick={handleContinue}
              disabled={refreshing}
              className="flex items-center justify-center gap-2 w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold hover:from-blue-500 hover:to-indigo-500 transition-all disabled:opacity-60"
            >
              {refreshing
                ? <><RefreshCw size={15} className="animate-spin" /> Extending…</>
                : <><RefreshCw size={15} /> Continue Session</>
              }
            </button>
            <button
              onClick={() => signOut()}
              className="flex items-center justify-center gap-2 w-full h-9 rounded-xl border border-white/10 text-slate-400 text-xs hover:bg-white/5 hover:text-white transition-all"
            >
              <LogOut size={13} /> Sign out now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
